// src/finance/useAutosave.ts
// Autosave avec debounce (spec § 2.3) : skippe la valeur d'hydratation, ne
// sauvegarde que les changements réels (comparaison JSON), expose le statut
// pour le badge « Enregistré ».
import { useEffect, useRef, useState } from 'react'

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error'

export function useAutosave<T>(
  value: T,
  save: (value: T) => Promise<unknown>,
  options?: { delay?: number | undefined; enabled?: boolean | undefined },
): AutosaveStatus {
  const delay = options?.delay ?? 800
  const enabled = options?.enabled ?? true
  const [status, setStatus] = useState<AutosaveStatus>('idle')
  // `JSON.stringify(undefined)` renvoie `undefined` et non une chaîne : le drapeau
  // d'hydratation est donc un booléen dédié, jamais une sentinelle sur la valeur.
  const serialized: string | undefined = JSON.stringify(value)
  const hydratedRef = useRef(false)
  const lastSavedRef = useRef<string | undefined>(undefined)
  const saveRef = useRef(save)
  saveRef.current = save

  useEffect(() => {
    if (!enabled) return
    // Première valeur *définie* vue = hydratation : on la considère déjà sauvegardée.
    // `undefined` (données pas encore chargées) ne consomme pas ce créneau, sinon
    // l'arrivée des vraies données passerait pour une édition de l'utilisateur.
    if (!hydratedRef.current) {
      if (serialized === undefined) return
      hydratedRef.current = true
      lastSavedRef.current = serialized
      return
    }
    // Retour à `undefined` après hydratation : rien à sauvegarder — `save(undefined)`
    // enverrait une requête sans corps, que le serveur rejetterait (400).
    if (serialized === undefined) return
    if (serialized === lastSavedRef.current) return

    // On capture la valeur du rendu qui a produit `serialized` (`value` n'est pas dans
    // les deps : c'est la comparaison JSON qui détecte les changements). Lire une ref
    // au déclenchement du timer renverrait parfois une valeur plus récente que
    // `serialized`, qui serait alors enregistrée puis sauvegardée une seconde fois.
    const pending = value
    const timer = setTimeout(() => {
      setStatus('saving')
      const snapshot = serialized
      saveRef.current(pending).then(
        () => {
          lastSavedRef.current = snapshot
          setStatus('saved')
        },
        () => {
          setStatus('error')
        },
      )
    }, delay)
    return () => clearTimeout(timer)
  }, [serialized, delay, enabled])

  return status
}
